import * as React from 'react';
import { useNotify, useRedirect } from 'react-admin';
import { useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { PageHeader } from '../../components/page-header/PageHeader';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form, FormikHelpers } from 'formik';
import { getBackendLanguage } from '../../core/i18n';
import { LocationService } from '../../services/location/locationService';
import { PromotionService } from '../../services/promotion/promotionService';
import { FormBackButton } from '../../components/back-button/FormBackButton';
import { ScrollToFirstError } from '../../components/form/ScrollToFirstError';
import * as Sentry from '@sentry/react';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { labelAboveSx } from '../../core/theme/formStyles';

interface PushLocation {
  id: number;
  name: string;
}

interface PushFormValues {
  locationId: string;
  title: string;
  body: string;
}

function usePushLocations() {
  const [locations, setLocations] = React.useState<PushLocation[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    new LocationService()
      .getLocations(getBackendLanguage())
      .then((data: any[]) => {
        if (cancelled) return;
        setLocations((data ?? []).map((location) => ({ id: location.id, name: location.name })));
      })
      .catch((error) => {
        console.error('Failed to load locations for push notification', error);
        Sentry.captureException(error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { locations, loading };
}

export default function LocationsPromotionPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const routerLocation = useLocation();
  const notify = useNotify();
  const redirect = useRedirect();
  const promotionService = new PromotionService();

  const { locations, loading: locationsLoading } = usePushLocations();

  const validationSchema = Yup.object({
    locationId: Yup.string().required(t('locationsManagement.promotion.locationRequired')),
    title: Yup.string().required(t('locationsManagement.promotion.titleRequired')),
    body: Yup.string().required(t('locationsManagement.promotion.bodyRequired')),
  });

  const initialValues: PushFormValues = {
    locationId: id ?? (locations.length === 1 ? String(locations[0].id) : ''),
    title: '',
    body: '',
  };

  const backTo = id ? '/locationsManagement/' + id + '/items' : '/locationsManagement';

  const handleSubmit = async (
    values: PushFormValues,
    { setSubmitting, resetForm }: FormikHelpers<PushFormValues>,
  ) => {
    try {
      await promotionService.sendPromotion({
        locationId: Number(values.locationId),
        title: values.title,
        body: values.body,
      });
      notify(t('notifications.promotionSent'), { type: 'success' });
      resetForm();

      if (id) redirect(backTo);
    } catch (error: any) {
      const message = error?.response?.data?.error ?? error?.message;
      notify(message, { type: 'error' });
      console.error(message);
      Sentry.captureException(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (locationsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 'var(--space-16)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, errors, touched, handleBlur, isSubmitting }) => {
        const selectedLocation = locations.find(
          (location) => String(location.id) === values.locationId,
        );
        const routerName = (routerLocation.state as { locationName?: string } | null)?.locationName;

        return (
          <Form>
            <ScrollToFirstError />
            <PageHeader
              back={<FormBackButton />}
              title={t('locationsManagement.promotion.title')}
              subtitle={selectedLocation?.name ?? routerName ?? t('locationsManagement.promotion.subtitle')}
            />
            <Box sx={{ px: PAGE_GUTTER, py: { xs: 2, md: 4 }, maxWidth: 640, ...labelAboveSx }}>
              <Stack spacing={2.5}>

                <FormControl
                  size="small"
                  fullWidth
                  error={touched.locationId && Boolean(errors.locationId)}
                >
                  <InputLabel shrink>{t('locationsManagement.promotion.locationField')}</InputLabel>
                  <Select
                    displayEmpty
                    name="locationId"
                    value={values.locationId}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    <MenuItem value="" disabled>
                      {t('coupons.fields.locationPlaceholder')}
                    </MenuItem>
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={String(location.id)}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <FormHelperText>
                    {touched.locationId && errors.locationId ? errors.locationId : ' '}
                  </FormHelperText>
                </FormControl>

                <TextField
                  name="title"
                  label={t('locationsManagement.promotion.titleField')}
                  size="small"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title ? errors.title : ' '}
                />

                <TextField
                  name="body"
                  label={t('locationsManagement.promotion.bodyField')}
                  multiline
                  rows={4}
                  size="small"
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 } }}
                  value={values.body}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.body && Boolean(errors.body)}
                  helperText={touched.body && errors.body ? errors.body : ' '}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', pt: 1 }}>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => redirect(backTo)}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      borderColor: 'var(--color-error)',
                      color: 'var(--color-error)',
                      '&:hover': {
                        borderColor: 'var(--color-error-hover)',
                        backgroundColor: 'var(--color-error-soft)',
                        color: 'var(--color-error-hover)',
                      },
                    }}
                  >
                    {t('common.cancel')}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-text-inverse)',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-hover)',
                        color: 'var(--color-text-inverse)',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={25} sx={{ color: 'white' }} />
                    ) : (
                      t('locationsManagement.promotion.send')
                    )}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
